import { Link } from "react-router";
import { ArrowUpRight, ExternalLink } from "lucide-react";
import { PARTNER_TIER_LABEL } from "@colab/shared";
import { HUBSPOT_PARTNER_DEAL_RELATIONSHIP_LABEL } from "@colab/shared";
import type { AdminPartnerAttribution } from "./use-admin-deal";

// SPA port of PartnerSummaryPanel — top of the deal drawer. Lists every
// partner attributed to the deal. A deal can have multiple attributions
// when referral + influencer overlap.

export function PartnerSummaryPanel({
  attributions,
}: {
  attributions: AdminPartnerAttribution[];
}) {
  if (attributions.length === 0) {
    return (
      <section
        aria-label="Partner"
        className="rounded-lg border border-dashed bg-muted/30 px-4 py-5 text-sm text-muted-foreground"
      >
        No partner attribution on this deal.
      </section>
    );
  }

  return (
    <section aria-label="Partner" className="space-y-2">
      {attributions.map((a) => (
        <article
          key={`${a.partner_id}-${a.relationship}`}
          className="rounded-lg border bg-card px-4 py-3"
        >
          <header className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                Partner · {HUBSPOT_PARTNER_DEAL_RELATIONSHIP_LABEL[a.relationship]}
              </p>
              <Link
                to={`/admin/partners/${a.partner_id}`}
                className="mt-0.5 inline-flex items-center gap-1 font-display text-base tracking-tight text-foreground hover:text-accent"
              >
                {a.partner_name}
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            {a.partner_hubspot_url && (
              <a
                href={a.partner_hubspot_url}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-[10px] font-medium text-accent hover:bg-accent/10"
                title="Open in HubSpot"
              >
                <ExternalLink className="h-3 w-3" />
                HubSpot
              </a>
            )}
          </header>
          <dl className="mt-2 grid grid-cols-3 gap-x-3 text-[11px]">
            {a.partner_tier && (
              <div>
                <dt className="text-[9px] uppercase tracking-wider text-muted-foreground">
                  Tier
                </dt>
                <dd className="text-foreground">
                  {PARTNER_TIER_LABEL[a.partner_tier]}
                </dd>
              </div>
            )}
            {a.partner_website && (
              <div className="min-w-0">
                <dt className="text-[9px] uppercase tracking-wider text-muted-foreground">
                  Website
                </dt>
                <dd className="truncate text-foreground">
                  {a.partner_website
                    .replace(/^https?:\/\//, "")
                    .replace(/\/$/, "")}
                </dd>
              </div>
            )}
            {a.partner_manager_name && (
              <div className="min-w-0">
                <dt className="text-[9px] uppercase tracking-wider text-muted-foreground">
                  Manager
                </dt>
                <dd className="truncate text-foreground">
                  {a.partner_manager_name}
                </dd>
              </div>
            )}
          </dl>
        </article>
      ))}
    </section>
  );
}
